import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import {
  portfolioService,
  ValuationReportResult,
} from '@/services/portfolio.service';
import { loanService } from '@/services/loan.service';
import { LoanResponse } from '@/types/api.types';
import { toast } from 'react-hot-toast';
import { ExcelExportButton } from '@/components/common/ExcelExportButton';
import { ButtonGroup, ButtonGroupText } from '@/components/ui/button-group';
import { ChartBar, Download } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { exportService } from '@/services/export.service';
import { formatCurrency as formatCurrencyHelper } from '@/utils/helpers';

const ValuationReportsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loanValuations, setLoanValuations] = useState<ValuationReportResult[]>(
    []
  );
  const [loans, setLoans] = useState<LoanResponse[]>([]);
  const [metadata, setMetadata] = useState<{
    source: string;
    total_loans: number;
    analysis_date: string;
    description: string;
    last_materialized_update?: string;
  } | null>(null);
  const [fairValueVariant, setFairValueVariant] = useState<
    'fair-value-report' | 'fair-value-report-with-commentary'
  >('fair-value-report');
  const [isExportingFairValue, setIsExportingFairValue] = useState(false);

  // Create a map of loan_id to currency
  // Try multiple keys: id, rp_system_id, and loan_name for matching
  const loanCurrencyMap = useMemo(() => {
    const map = new Map<number | string, 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD'>();
    loans.forEach((loan) => {
      if (loan.currency) {
        // Map by id, rp_system_id, and loan_name to handle different ID formats
        map.set(loan.id, loan.currency);
        map.set(loan.rp_system_id, loan.currency);
        if (loan.loan_name) {
          map.set(loan.loan_name, loan.currency);
        }
      }
    });
    console.log('Loan currency map:', Array.from(map.entries()));
    return map;
  }, [loans]);

  useEffect(() => {
    loadValuationData();
  }, []);

  const loadValuationData = async () => {
    try {
      setLoading(true);

      // Load valuation reports first (required)
      const valuationReports = await portfolioService.getValuationReports();
      setLoanValuations(valuationReports);

      // Try to load loans for currency mapping (optional - if it fails, we'll use USD default)
      try {
        // Load all loans - may need multiple pages
        let allLoans: LoanResponse[] = [];
        let currentPage = 1;
        let hasMore = true;
        
        while (hasMore) {
          const loansData = await loanService.getLoansPaginated({ 
            page: currentPage, 
            page_size: 100 
          });
          if (loansData.items && loansData.items.length > 0) {
            allLoans = [...allLoans, ...loansData.items];
            hasMore = loansData.has_next || false;
            currentPage++;
          } else {
            hasMore = false;
          }
        }
        
        console.log(`Loaded ${allLoans.length} loans for currency mapping`);
        console.log('Sample loans:', allLoans.slice(0, 3).map(l => ({ 
          id: l.id, 
          rp_system_id: l.rp_system_id, 
          loan_name: l.loan_name, 
          currency: l.currency 
        })));
        console.log('Sample valuation reports:', valuationReports.slice(0, 3).map(v => ({ 
          loan_id: v.loan_id, 
          loan_name: v.loan_name 
        })));
        
        setLoans(allLoans);
      } catch (loansError) {
        console.warn('Failed to load loans for currency mapping, using USD default:', loansError);
        // Continue without loans - currency will default to USD
        setLoans([]);
      }

      // Create metadata from the data
      setMetadata({
        source: 'Consolidated Valuation Reports',
        total_loans: valuationReports.length,
        analysis_date:
          valuationReports[0]?.valuation_date ||
          new Date().toISOString().split('T')[0],
        description:
          'Complete loan pricing engine output results from RPX loan portfolio analysis',
        last_materialized_update: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error loading valuation data:', error);
      toast.error('Failed to load valuation data');
    } finally {
      setLoading(false);
    }
  };

  const handleFairValueExport = async (
    variant: 'fair-value-report' | 'fair-value-report-with-commentary'
  ) => {
    if (isExportingFairValue) return;

    setIsExportingFairValue(true);

    const toastId = toast.loading(
      variant === 'fair-value-report'
        ? 'Generating fair value report... This may take some time.'
        : 'Generating fair value report with commentary... This may take some time.',
      { duration: Infinity }
    );

    try {
      const isCommentary = variant === 'fair-value-report-with-commentary';

      const blob = await exportService.exportFairValueReportToExcel({
        report_type: isCommentary ? 'commentary' : 'standard',
      });

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = isCommentary
        ? `fair-value-report-with-commentary-${timestamp}.xlsx`
        : `fair-value-report-${timestamp}.xlsx`;

      exportService.downloadFile(blob, filename);

      toast.dismiss(toastId);
      toast.success('Export completed successfully!', { duration: 3000 });
    } catch (error) {
      console.error('Fair value export error:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to export fair value report. Please try again.';
      toast.error(errorMessage, { id: toastId, duration: 5000 });
    } finally {
      setIsExportingFairValue(false);
    }
  };


  // Use global formatCurrency from helpers (defaults to USD for aggregated data)
  const formatCurrency = (value: number, currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' = 'USD') => {
    return formatCurrencyHelper(value, 0, currency);
  };

  // Clean price percentage formatting (5 decimal places)
  const formatCleanPricePercent = (pricePct: number | null | undefined) => {
    if (pricePct == null) return '—';
    // Format with 5 decimal places
    return `${pricePct.toFixed(5)}%`;
  };

  // Yield percentage: 3 decimal places
  const formatYieldPercentage = (value: number | null | undefined) => {
    if (value == null) return '—';
    return `${value.toFixed(3)}%`;
  };

  // Duration: 2 decimal places
  const formatDuration = (value: number | null | undefined) => {
    if (value == null) return '—';
    return `${value.toFixed(2)} years`;
  };

  // WAL: 2 decimal places
  const formatWAL = (value: number | null | undefined) => {
    if (value == null) return '—';
    return `${value.toFixed(2)} years`;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-inner">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-gray-600">
              Loading valuation reports...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-inner">
        {/* Header */}
        <div className="mb-2">
          <h1
            className="text-2xl font-bold mb-2"
            data-test-id="reports-title"
          >
            Valuation Reports
          </h1>
          {metadata && (
            <p
              className="section-subtitle"
              data-test-id="reports-subtitle"
            >
              Analysis Date: {metadata.analysis_date} | Total Loans:{' '}
              {metadata.total_loans} | Source: {metadata.source}
            </p>
          )}
        </div>

        {/* Valuation Metrics Summary Table - Clean Price (excluding accrued interest) & Market Yield */}
        <Card className="py-6 px-4 lg:py-8 lg:px-6">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-semibold text-gray-900">
              Valuation Metrics Summary
            </h3>
            <div className="flex items-center gap-3 justify-end">
              <ButtonGroup>
                <ButtonGroupText asChild>
                  <Label
                    htmlFor="name"
                    className="bg-primary text-primary-foreground shadow-xs border border-primary/20 rounded-md px-4 py-2 h-11 flex items-center justify-center"
                  >
                    Export:
                  </Label>
                </ButtonGroupText>
                <div className="flex items-center gap-0">
                  <Select
                    value={fairValueVariant}
                    disabled={isExportingFairValue}
                    onValueChange={(val) => {
                      const variant =
                        val as
                          | 'fair-value-report'
                          | 'fair-value-report-with-commentary';
                      setFairValueVariant(variant);
                    }}
                  >
                    <SelectTrigger
                      className="w-64 h-11 bg-yellow-400 border border-yellow-500 rounded-none px-3 shadow-none focus-visible:ring-0 focus-visible:outline-none text-primary-foreground [&>span]:text-primary-foreground [&>span]:text-sm [&>span]:font-semibold"
                      data-slot="select-trigger"
                    >
                      <SelectValue placeholder="Select report type" />
                    </SelectTrigger>
                    <SelectContent className="[&_[data-test-id='select-item-check']]:hidden">
                      <SelectItem value="fair-value-report">
                        Fair Value Report
                      </SelectItem>
                      <SelectItem value="fair-value-report-with-commentary">
                        Fair Value Report with Commentary
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => handleFairValueExport(fairValueVariant)}
                    disabled={isExportingFairValue}
                    className="h-11 w-11 rounded-none border border-yellow-500 bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center p-0"
                    title="Download selected report"
                  >
                    {isExportingFairValue ? (
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <ExcelExportButton
                  exportType="portfolio-summary-report"
                  label="Portfolio Summary Report"
                  icon={<ChartBar className="h-4 w-4" />}
                  variant="default"
                  size="lg"
                  className="h-11"
                />
                <ExcelExportButton
                  exportType="complete-report"
                  label="Complete Report"
                  variant="default"
                  size="lg"
                  className="h-11"
                />
              </ButtonGroup>
            </div>
          </div>
          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table className="relative table-responsive min-w-full  divide-y divide-gray-200 overflow-hidden">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Loan Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Current Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Fair Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Clean Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Yield
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    WAL
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loanValuations.map((loan) => {
                  // Try to find currency by loan_id, then by loan_name
                  const currency = loanCurrencyMap.get(loan.loan_id) || 
                                  loanCurrencyMap.get(loan.loan_name) || 
                                  'USD';
                  return (
                    <tr key={loan.loan_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                        {loan.loan_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        {formatCurrency(loan.current_balance, currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                        {formatCurrency(loan.fair_value_clean, currency)}
                      </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                      {formatCleanPricePercent(loan.price_clean_pct)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                      {formatYieldPercentage(loan.component_yield_pct)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                      {formatDuration(loan.modified_duration_years)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatWAL(loan.wal_years)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ValuationReportsPage;
